import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, ArrowRight } from "lucide-react";
import { WORKS } from "@/constants/testIds";
import { useT } from "@/i18n";
import { CASES } from "@/data/content";

export default function Works() {
  const ref = useRef(null);
  const { t, lang } = useT();
  const navigate = useNavigate();
  const [active, setActive] = useState(0);

  // Auto-rotate every 6s
  useEffect(() => {
    const id = setInterval(() => {
      setActive((a) => (a + 1) % CASES.length);
    }, 6000);
    return () => clearInterval(id);
  }, []);

  const slide = CASES[active];

  const next = () => setActive((a) => (a + 1) % CASES.length);
  const prev = () => setActive((a) => (a - 1 + CASES.length) % CASES.length);

  return (
    <div
      ref={ref}
      data-testid={WORKS.root}
      className="relative w-full h-screen overflow-hidden grain flex items-center justify-center transition-[background] duration-[900ms] ease-out px-6 md:px-12 lg:px-20 py-16 md:py-20"
      style={{ background: slide.bg }}
    >
      {/* Subtle accent orb */}
      <div
        className="absolute pointer-events-none rounded-full transition-all duration-[1200ms] ease-out"
        style={{
          width: 800,
          height: 800,
          top: "-20%",
          right: "-10%",
          background: `radial-gradient(circle, ${slide.accent} 0%, transparent 65%)`,
          opacity: 0.12,
          filter: "blur(60px)",
        }}
      />

      <div className="relative z-10 w-full max-w-[1400px] max-h-[75vh] overflow-y-auto px-6 md:px-10 py-12 md:py-16">
        <div className="flex flex-col h-full text-white">
          {/* Header with counter */}
          <div className="flex items-center justify-between mb-12 md:mb-16">
            <div className="flex flex-col gap-2">
              <span className="eyebrow text-white/40">{t("works.eyebrow")}</span>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-semibold">
                Selected <span style={{ color: slide.accent }}>Works</span>
              </h2>
            </div>
            <div className="font-mono text-sm text-white/50">
              {String(active + 1).padStart(2, "0")} / {String(CASES.length).padStart(2, "0")}
            </div>
          </div>

          {/* Main content */}
          <div 
            key={slide.slug + "-content"}
            className="flex-1 flex flex-col lg:flex-row gap-10 lg:gap-16 fade-swap"
          >
            {/* Left: Main info */}
            <div className="flex-1 flex flex-col justify-between gap-8">
              {/* Scope tags */}
              <div className="flex flex-wrap gap-2">
                {slide.scope.map((s) => (
                  <span
                    key={s}
                    className="px-3 py-1.5 rounded-full text-[0.65rem] font-mono uppercase tracking-wider border border-white/20 text-white/70"
                  >
                    {s}
                  </span>
                ))}
              </div>

              {/* Title */}
              <h3 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold leading-tight text-white">
                {slide.title[lang]}
              </h3>

              {/* Summary */}
              <div className="space-y-4">
                <div className="eyebrow text-white/40">
                  {lang === "id" ? "Sinopsis" : "Synopsis"}
                </div>
                <p 
                  className="text-white/75 text-base md:text-lg leading-relaxed max-w-2xl border-l-2 pl-6"
                  style={{ borderColor: `${slide.accent}90` }}
                >
                  {slide.summary[lang]}
                </p>
              </div>

              {/* Meta info */}
              <div className="flex flex-wrap items-center gap-4 text-sm text-white/50 font-mono">
                <span>{slide.client}</span>
                <span className="w-1 h-1 rounded-full bg-white/30" />
                <span>{slide.year}</span>
                <span className="w-1 h-1 rounded-full bg-white/30" />
                <span>{slide.industry[lang]}</span>
              </div>
            </div>

            {/* Right: Metric & CTA */}
            <div className="lg:w-80 flex flex-col justify-between gap-8">
              {/* Impact metric */}
              <div className="space-y-4">
                <div className="eyebrow text-white/40">{t("works.impact")}</div>
                <div 
                  className="text-3xl md:text-4xl font-display font-bold leading-tight"
                  style={{ color: slide.accent }}
                >
                  {slide.metric[lang]}
                </div>
                <div 
                  className="h-0.5 w-20"
                  style={{ background: slide.accent }}
                />
              </div>

              {/* CTA Button */}
              <button
                type="button"
                onClick={() => navigate(`/works/${slide.slug}`)}
                data-testid={`works-view-${slide.slug}`}
                className="group inline-flex items-center justify-center gap-3 font-mono text-sm uppercase tracking-wider text-white border border-white/30 rounded-full px-6 py-4 hover:bg-white hover:text-black transition-all duration-300"
              >
                {t("works.viewCase")}
                <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </div>
          </div>

          {/* Navigation */}
          <div className="mt-12 md:mt-16 pt-8 border-t border-white/15 flex items-center justify-between gap-6">
            <button
              type="button"
              data-testid={WORKS.prev}
              onClick={prev}
              aria-label="Previous case"
              className="flex-none w-12 h-12 rounded-full border border-white/25 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 hover:border-white/60 transition-all"
            >
              <ArrowLeft size={18} />
            </button>

            {/* Simple dots navigation */}
            <div className="flex-1 flex items-center justify-center gap-3">
              {CASES.map((c, i) => (
                <button
                  key={c.slug}
                  type="button"
                  data-testid={WORKS.slide(i)}
                  onClick={() => setActive(i)}
                  onMouseEnter={() => setActive(i)}
                  className="group relative"
                  aria-label={`View ${c.title[lang]}`}
                >
                  <div
                    className="w-2 h-2 rounded-full transition-all duration-300"
                    style={{
                      background: i === active ? slide.accent : "rgba(255,255,255,0.25)",
                      transform: i === active ? "scale(1.5)" : "scale(1)",
                    }}
                  />
                  {/* Tooltip on hover */}
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 px-3 py-1.5 bg-black/90 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    {c.title[lang]}
                  </div>
                </button>
              ))}
            </div>

            <button
              type="button"
              data-testid={WORKS.next}
              onClick={next}
              aria-label="Next case"
              className="flex-none w-12 h-12 rounded-full border border-white/25 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 hover:border-white/60 transition-all"
            >
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .fade-swap { animation: wFade 600ms ease-out both; }
        @keyframes wFade {
          0% { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
